// client/src/utils/excelUtils.js
export const readWB = (file) => {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) =>
      res(window.XLSX.read(new Uint8Array(e.target.result), { type: "array" }));
    reader.onerror = rej;
    reader.readAsArrayBuffer(file);
  });
};
export const findHeader = (rows, keys) => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i].map((c) => (c || "").toString().trim());
    if (keys.every((k) => row.includes(k))) return i;
  }
  return -1;
};

export const formatExcelDate = (v) => {
  if (typeof v === "number") {
    // Excel stores dates as the number of days since January 1, 1900
    const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30); // December 30, 1899 UTC
    const utcDate = new Date(EXCEL_EPOCH_UTC + v * 86400 * 1000);

    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
  if (v instanceof Date) {
    const year = v.getUTCFullYear();
    const month = String(v.getUTCMonth() + 1).padStart(2, "0");
    const day = String(v.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof v === "string") {
    if (v.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return v;
    }
    if (v.includes("T")) {
      return v.split("T")[0];
    }
    // Handle DD/MM/YYYY format (Thai format) - PRIORITY FORMAT
    const thaiDateMatch = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (thaiDateMatch) {
      const [, dayStr, monthStr, year] = thaiDateMatch;
      const day = parseInt(dayStr);
      const month = parseInt(monthStr);

      // For Thai format, always treat as DD/MM/YYYY
      // Only switch to MM/DD if day > 31 (impossible) or month > 12
      if (month > 12 && day <= 12) {
        // Switch: it must be MM/DD format
        return `${year}-${dayStr.padStart(2, "0")}-${monthStr.padStart(
          2,
          "0"
        )}`;
      } else {
        // Default to DD/MM/YYYY (Thai format)
        return `${year}-${monthStr.padStart(2, "0")}-${dayStr.padStart(
          2,
          "0"
        )}`;
      }
    }
    const parsed = new Date(v);
    if (!isNaN(parsed.getTime())) {
      const year = parsed.getUTCFullYear();
      const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
      const day = String(parsed.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }
  return v;
};

export const parseTax = (wb) => {
  const rows = window.XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
    header: 1,
    raw: true,
  });
  const keys = [
    "Sales Route",
    "Calendar Day",
    "Invoice No",
    "Outlet",
    "NIV",
    "VAT",
    "Total Sales Amt Inc. Tax",
  ];
  const hr = findHeader(rows, keys);
  const hdr = rows[hr],
    idx = hdr.reduce((o, c, i) => ((o[c] = i), o), {});
  return rows
    .slice(hr + 1)
    .filter((r) => r[idx["Outlet"]])
    .map((r) => ({
      "Outlet Code": r[idx["Outlet"]] || "",
      "Sales Route": r[idx["Sales Route"]] || "",
      "Calendar Day": formatExcelDate(r[idx["Calendar Day"]]),
      "Invoice No": (r[idx["Invoice No"]] || "").toString(),
      NIV: Number(r[idx["NIV"]] || 0),
      VAT: Number(r[idx["VAT"]] || 0),
      "Total Sales Amt Inc. Tax": Number(
        r[idx["Total Sales Amt Inc. Tax"]] || 0
      ),
      GSV: r[idx["GSV"]] || "",
      "Outlet Name Tax": r[idx["Outlet"] + 1] || "",
    }));
};

export const mergeMaster = (taxData, d10, d19) => {
  const map10 = {},
    map19 = {};
  d10.forEach((r) => (map10[r["Outlet Code"]] = r));
  d19.forEach((r) => (map19[r["Outlet Code"]] = r));

  // Exact column structure as specified (16 columns total)
  const cols = [
    "Site",
    "Site Name",
    "Outlet Code",
    "Outlet Name",
    "Outlet Type Description",
    "Outlet SubType Description",
    "Sales Route",
    "Status",
    "Opening Date",
    "Closing Date",
    "GSV",
    "Calendar Day",
    "Invoice No",
    "NIV",
    "VAT",
    "Total Sales Amt Inc. Tax",
  ];

  console.log(
    `🔄 Merging data using exact column structure (${cols.length} columns):`,
    cols
  );

  const data = taxData.map((t) => {
    const a10 = map10[t["Outlet Code"]] || {},
      a19 = map19[t["Outlet Code"]] || {};

    const mergedRecord = {
      Site: a10.Site || "",
      "Site Name": a19["Site Name"] || "",
      "Outlet Code": t["Outlet Code"],
      "Outlet Name":
        a10["Outlet Name 3110"] ||
        a19["Outlet Name 3119"] ||
        t["Outlet Name Tax"] ||
        "",
      "Outlet Type Description": a10["Outlet Type Description"] || "",
      "Outlet SubType Description": a10["Outlet SubType Description"] || "",
      "Sales Route": t["Sales Route"],
      Status: a19.Status || "",
      "Opening Date": a19["Opening Date"] || "",
      "Closing Date": a19["Closing Date"] || "",
      GSV: t["GSV"] || "",
      "Calendar Day": t["Calendar Day"],
      "Invoice No": t["Invoice No"],
      NIV: parseFloat(t["NIV"]) || 0,
      VAT: parseFloat(t["VAT"]) || 0,
      "Total Sales Amt Inc. Tax":
        parseFloat(t["Total Sales Amt Inc. Tax"]) || 0,
    };

    return mergedRecord;
  });

  console.log(
    `✅ Merged ${data.length} records with ${cols.length} columns each`
  );

  // Verify first record has all columns
  if (data.length > 0) {
    const firstRecord = data[0];
    const actualColumns = Object.keys(firstRecord);
    console.log(
      `📋 First record columns (${actualColumns.length}):`,
      actualColumns
    );

    // Check for missing columns
    const missingColumns = cols.filter((col) => !actualColumns.includes(col));
    if (missingColumns.length > 0) {
      console.warn(`⚠️ Missing columns:`, missingColumns);
    }
  }

  return { cols, data };
};

export const parseAR04 = (wb) => {
  const rows = window.XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
    header: 1,
    raw: true,
  });

  const keys = [
    "วันที่",
    "เลขที่ใบกำกับ",
    "รหัสลูกค้า",
    "เลขที่บัญชี",
    "ค่าสินค้า/บริการ",
    "ภาษีมูลค่าเพิ่ม",
  ];

  const hr = findHeader(rows, keys);
  if (hr === -1) {
    console.warn("Could not find AR04 header row with keys:", keys);
    return [];
  }

  const hdr = rows[hr];
  const idx = hdr.reduce((o, c, i) => ((o[c] = i), o), {});

  return rows
    .slice(hr + 1)
    .filter((r) => r[idx["รหัสลูกค้า"]]) // Filter rows with customer code
    .map((r) => ({
      วันที่: formatExcelDate(r[idx["วันที่"]]),
      เลขที่ใบกำกับ: (r[idx["เลขที่ใบกำกับ"]] || "")
        .toString()
        .replace(/^D/i, ""), // Remove 'D' prefix if exists
      รหัสลูกค้า: r[idx["รหัสลูกค้า"]] || "",
      เลขที่บัญชี: r[idx["เลขที่บัญชี"]] || "",
      "ค่าสินค้า/บริการ": Number(r[idx["ค่าสินค้า/บริการ"]] || 0),
      ภาษีมูลค่าเพิ่ม: Number(r[idx["ภาษีมูลค่าเพิ่ม"]] || 0),
    }));
};

export const compareAR04WithMaster = (ar04Data, masterData) => {
  console.log(
    `🔄 Starting comparison: ${ar04Data.length} AR04 records vs ${masterData.length} master records`
  );

  // Validate input data
  if (!ar04Data || ar04Data.length === 0) {
    throw new Error("AR04 data is empty or invalid");
  }
  if (!masterData || masterData.length === 0) {
    throw new Error("Master data is empty or invalid");
  }

  // Helper function to normalize dates for comparison
  const normalizeDate = (dateValue) => {
    if (!dateValue) return "";

    // Convert to string and extract date part only
    const dateStr = dateValue.toString();

    // If it's already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }

    // If it contains timestamp, extract date part
    if (dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }

    // If it's a Date object, format it
    if (dateValue instanceof Date) {
      return dateValue.toISOString().split("T")[0];
    }

    return dateStr;
  };

  // Route mapping for AR04 comparison
  const routeMapping = {
    256201: "4010.1",
    256202: "4010.2",
    256203: "4010.3",
    256204: "4010.4",
    256205: "4010.5",
    256206: "4010.6",
    256207: "4010.7",
    256208: "4010.8",
    256209: "4010.9",
    256210: "4010.90",
    256211: "4010.91",
  };

  // Create master data lookup for faster comparison
  const masterLookup = {};
  masterData.forEach((m) => {
    const invoiceKey = (m["Invoice No"] || "").toString().replace(/^D/i, "");
    if (invoiceKey) {
      masterLookup[invoiceKey] = m;
    }
  });

  console.log(
    `📊 Created master lookup with ${
      Object.keys(masterLookup).length
    } invoice entries`
  );

  const data = ar04Data.map((a, index) => {
    const inv = (a["เลขที่ใบกำกับ"] || "").toString().replace(/^D/i, "");

    // Find matching master record by invoice number
    const m = masterLookup[inv] || {};

    const nv = parseFloat(m["NIV"]) || 0;
    const vt = parseFloat(m["VAT"]) || 0;
    const arNiv = parseFloat(a["ค่าสินค้า/บริการ"]) || 0;
    const arVat = parseFloat(a["ภาษีมูลค่าเพิ่ม"]) || 0;

    // Normalize dates for comparison
    const arDate = normalizeDate(a["วันที่"]);
    const masterDate = normalizeDate(m["Calendar Day"]);

    // Calculate expected account number from route mapping
    const salesRoute = m["Sales Route"];
    const expectedAcct = routeMapping[salesRoute] || salesRoute;

    // Check for differences with improved date comparison
    const diffs = {
      "Calendar Day": arDate !== masterDate,
      "Invoice No":
        inv !== (m["Invoice No"] || "").toString().replace(/^D/i, ""),
      "Sales Route": expectedAcct
        ? a["เลขที่บัญชี"] !== expectedAcct
        : a["เลขที่บัญชี"] !== salesRoute,
      NIV: Math.abs(arNiv - nv) > 0.01,
      VAT: Math.abs(arVat - vt) > 0.01,
    };

    // Generate remarks
    const remark = [];
    if (!m["Invoice No"]) remark.push("ไม่พบ Invoice ใน MASTER");
    if (diffs["Calendar Day"]) {
      // More detailed date comparison message
      remark.push(`วันที่ ไม่ตรง (AR04: ${arDate}, MASTER: ${masterDate})`);
    }
    if (diffs["Sales Route"])
      remark.push(`เลขที่บัญชี ควรเป็น ${expectedAcct || salesRoute}`);
    if (diffs["NIV"])
      remark.push(`ค่าสินค้า/บริการ ต่าง ${(arNiv - nv).toFixed(2)}`);
    if (diffs["VAT"])
      remark.push(`ภาษีมูลค่าเพิ่ม ต่าง ${(arVat - vt).toFixed(2)}`);

    // Build result row with database-friendly field names
    const row = {
      // Use record_index instead of row_number to avoid SQL reserved keyword
      record_index: index + 1,
      invoice_no_ar04: inv || "",
      invoice_no_master: m["Invoice No"] || "",
      date_ar04: arDate || "",
      date_master: masterDate || "",
      customer_code_ar04: a["รหัสลูกค้า"] || "",
      outlet_code_master: m["Outlet Code"] || "",
      outlet_name_master: m["Outlet Name"] || "",
      account_no_ar04: a["เลขที่บัญชี"] || "",
      sales_route_master: m["Sales Route"] || "",

      // Amount fields with proper decimal handling
      service_amount_ar04: Number(arNiv.toFixed(2)),
      niv_master: Number(nv.toFixed(2)),
      niv_difference: Number((arNiv - nv).toFixed(2)),
      vat_amount_ar04: Number(arVat.toFixed(2)),
      vat_master: Number(vt.toFixed(2)),
      vat_difference: Number((arVat - vt).toFixed(2)),

      // Remark and flags
      remark: remark.length > 0 ? remark.join(", ") : "ตรงกันทุกประการ",
      has_difference: Object.values(diffs).some((d) => d),

      // Keep original structure for display (with Thai column names)
      เลขที่ใบกำกับ: inv,
      "Invoice No": m["Invoice No"] || "",
      วันที่: arDate,
      "Calendar Day": masterDate,
      รหัสลูกค้า: a["รหัสลูกค้า"] || "",
      "Outlet Code": m["Outlet Code"] || "",
      "Outlet Name": m["Outlet Name"] || "",
      เลขที่บัญชี: a["เลขที่บัญชี"] || "",
      "Sales Route": m["Sales Route"] || "",
      "ค่าสินค้า/บริการ": arNiv,
      NIV: nv,
      "NIV Diff": arNiv - nv,
      ภาษีมูลค่าเพิ่ม: arVat,
      VAT: vt,
      "VAT Diff": arVat - vt,
      Remark: remark.length > 0 ? remark.join(", ") : "ตรงกันทุกประการ",

      // Add difference flags for highlighting
      _diff: diffs,
    };

    return row;
  });

  const cols = [
    "เลขที่ใบกำกับ",
    "Invoice No",
    "วันที่",
    "Calendar Day",
    "รหัสลูกค้า",
    "Outlet Code",
    "Outlet Name",
    "เลขที่บัญชี",
    "Sales Route",
    "ค่าสินค้า/บริการ",
    "NIV",
    "NIV Diff",
    "ภาษีมูลค่าเพิ่ม",
    "VAT",
    "VAT Diff",
    "Remark",
  ];

  console.log(`✅ Generated ${data.length} comparison results`);

  // Debug log for date comparison
  if (data.length > 0) {
    const sampleRow = data[0];
    const sampleAR04 = ar04Data[0];
    const sampleMaster = masterData[0];
    console.log(`📅 Date comparison sample:`, {
      ar04Date: sampleRow.วันที่,
      masterDate: sampleRow["Calendar Day"],
      ar04Raw: sampleAR04 ? sampleAR04["วันที่"] : "N/A",
      masterRaw: sampleMaster ? sampleMaster["Calendar Day"] : "N/A",
      dateMatch: sampleRow.วันที่ === sampleRow["Calendar Day"],
    });
  }

  return { cols, data };
};
